// DAS-2107 / DAS-2150: "Your ESG" modal for PortfolioAnalysisView
// Per-ticker ESG table + portfolio-level summary panel (per-provider, never combined).
// Badge styling replicates AccountPortfolioModal exactly.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, Tooltip, CircularProgress,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../functions/api';
import ClickableTickerChip from './ClickableTickerChip';

const MSCI_COLORS = {
  'AAA': '#52c41a', 'AA': '#52c41a', 'A': '#73d13d',
  'BBB': '#faad14', 'BB': '#fa8c16', 'B': '#fa8c16',
  'CCC': '#ff4d4f',
};

const saColor = (v) => v <= 20 ? '#52c41a' : v <= 30 ? '#faad14' : '#ff4d4f';
const refColor = (v) => v >= 70 ? '#52c41a' : v >= 50 ? '#faad14' : '#ff4d4f';

const badgeStyle = (bg) => ({
  backgroundColor: bg,
  color: 'white',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 'bold',
  display: 'inline-block',
});

// ─── Portfolio-level ESG summary (DAS-2150) ─────────────────────

const SA_RISK_LEVELS = [
  { max: 10, label: 'Negligible', color: '#2e7d32' },
  { max: 20, label: 'Low', color: '#52c41a' },
  { max: 30, label: 'Medium', color: '#faad14' },
  { max: 40, label: 'High', color: '#fa8c16' },
  { max: Infinity, label: 'Severe', color: '#ff4d4f' },
];

const REF_LEVELS = [
  { min: 90, label: 'Leader', color: '#2e7d32' },
  { min: 75, label: 'Above Average', color: '#52c41a' },
  { min: 50, label: 'Average', color: '#faad14' },
  { min: 25, label: 'Below Average', color: '#fa8c16' },
  { min: 0, label: 'Laggard', color: '#ff4d4f' },
];

const getSaRisk = (v) => SA_RISK_LEVELS.find(l => v <= l.max) || SA_RISK_LEVELS[SA_RISK_LEVELS.length - 1];
const getRefLevel = (v) => REF_LEVELS.find(l => v >= l.min) || REF_LEVELS[REF_LEVELS.length - 1];

const MSCI_GRADES = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC'];
const MSCI_GRADE_COLORS = {
  'AAA': '#1b5e20', 'AA': '#2e7d32', 'A': '#52c41a',
  'BBB': '#faad14',
  'BB': '#fa8c16', 'B': '#e65100',
  'CCC': '#ff4d4f',
};

function computeESGSummary(rows, data) {
  if (!rows.length) return null;

  const totalPortfolioValue = (data || []).reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
  const useWeights = totalPortfolioValue > 0;

  const getWeight = (ticker) => {
    if (!useWeights) return 1;
    const pos = (data || []).find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());
    return parseFloat(pos?.Value_eur) || 0;
  };

  const denominator = useWeights ? totalPortfolioValue : rows.length;

  // Sustainalytics — weighted average risk rating
  const saRows = rows.filter(r => r.sustainalytics != null);
  let sa = null;
  if (saRows.length > 0) {
    const totalWeight = saRows.reduce((s, r) => s + getWeight(r.ticker), 0);
    const avg = totalWeight > 0
      ? saRows.reduce((s, r) => s + r.sustainalytics * getWeight(r.ticker), 0) / totalWeight
      : null;
    sa = {
      avg,
      risk: avg != null ? getSaRisk(avg) : null,
      coverage: {
        count: saRows.length,
        total: rows.length,
        valuePct: denominator > 0 ? (totalWeight / denominator * 100) : 0,
      },
    };
  }

  // MSCI — grade distribution (categorical, not averaged)
  const msciRows = rows.filter(r => r.msci != null);
  let msci = null;
  if (msciRows.length > 0) {
    const dist = {};
    let totalWeight = 0;
    for (const g of MSCI_GRADES) dist[g] = 0;
    for (const r of msciRows) {
      const w = getWeight(r.ticker);
      totalWeight += w;
      if (dist[r.msci] != null) dist[r.msci] += w;
    }
    const distPct = {};
    for (const g of MSCI_GRADES) {
      distPct[g] = totalWeight > 0 ? (dist[g] / totalWeight * 100) : 0;
    }
    msci = {
      dist: distPct,
      coverage: {
        count: msciRows.length,
        total: rows.length,
        valuePct: denominator > 0 ? (totalWeight / denominator * 100) : 0,
      },
    };
  }

  // Refinitiv — weighted average score
  const refRows = rows.filter(r => r.refinitiv != null);
  let ref = null;
  if (refRows.length > 0) {
    const totalWeight = refRows.reduce((s, r) => s + getWeight(r.ticker), 0);
    const avg = totalWeight > 0
      ? refRows.reduce((s, r) => s + r.refinitiv * getWeight(r.ticker), 0) / totalWeight
      : null;
    ref = {
      avg,
      level: avg != null ? getRefLevel(avg) : null,
      coverage: {
        count: refRows.length,
        total: rows.length,
        valuePct: denominator > 0 ? (totalWeight / denominator * 100) : 0,
      },
    };
  }

  return { sa, msci, ref, useWeights };
}

const CoverageLine = ({ coverage, useWeights }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
    {coverage.count} of {coverage.total} positions
    {useWeights && ` (${coverage.valuePct.toFixed(0)}% of portfolio value)`}
  </Typography>
);

const ESGSummaryPanel = ({ summary }) => {
  if (!summary) return null;
  const { sa, msci, ref, useWeights } = summary;
  if (!sa && !msci && !ref) return null;

  return (
    <Box display="flex" gap={2} mb={3} flexWrap="wrap">
      {sa && sa.avg != null && (
        <Paper variant="outlined" sx={{ flex: '1 1 200px', p: 2, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
            Sustainalytics ESG Risk
          </Typography>
          <Box display="flex" alignItems="baseline" gap={1} mt={0.5}>
            <span style={badgeStyle(sa.risk.color)}>{sa.avg.toFixed(1)}</span>
            <Typography variant="body2" sx={{ fontWeight: 600, color: sa.risk.color }}>
              {sa.risk.label}
            </Typography>
          </Box>
          <CoverageLine coverage={sa.coverage} useWeights={useWeights} />
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
            Lower is better · {useWeights ? 'Value-weighted' : 'Equal-weighted'} average
          </Typography>
        </Paper>
      )}

      {msci && (
        <Paper variant="outlined" sx={{ flex: '1 1 280px', p: 2, minWidth: 250 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
            MSCI ESG Rating Distribution
          </Typography>
          <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden', mt: 1, mb: 0.5 }}>
            {MSCI_GRADES.map(grade => {
              const pct = msci.dist[grade];
              if (pct <= 0) return null;
              return (
                <Tooltip key={grade} title={`${grade}: ${pct.toFixed(1)}%`} arrow>
                  <Box
                    sx={{
                      width: `${pct}%`,
                      bgcolor: MSCI_GRADE_COLORS[grade],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'width 0.3s',
                      minWidth: pct > 0 ? 2 : 0,
                    }}
                  >
                    {pct >= 12 && (
                      <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>
                        {grade}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
            {MSCI_GRADES.filter(g => msci.dist[g] > 0).map(g => (
              <Chip
                key={g}
                label={`${g} ${msci.dist[g].toFixed(0)}%`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: `${MSCI_GRADE_COLORS[g]}18`,
                  color: MSCI_GRADE_COLORS[g],
                  border: `1px solid ${MSCI_GRADE_COLORS[g]}40`,
                }}
              />
            ))}
          </Box>
          <CoverageLine coverage={msci.coverage} useWeights={useWeights} />
        </Paper>
      )}

      {ref && ref.avg != null && (
        <Paper variant="outlined" sx={{ flex: '1 1 200px', p: 2, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
            Refinitiv ESG Score
          </Typography>
          <Box display="flex" alignItems="baseline" gap={1} mt={0.5}>
            <span style={badgeStyle(ref.level.color)}>{ref.avg.toFixed(1)}</span>
            <Typography variant="body2" sx={{ fontWeight: 600, color: ref.level.color }}>
              {ref.level.label}
            </Typography>
          </Box>
          <CoverageLine coverage={ref.coverage} useWeights={useWeights} />
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
            Higher is better · {useWeights ? 'Value-weighted' : 'Equal-weighted'} average
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// ─── Per-ticker badge row ───────────────────────────────────────

const ESGBadges = ({ sustainalytics, msci, refinitiv }) => {
  const items = [];

  if (sustainalytics != null && sustainalytics !== 'N/A') {
    const v = parseFloat(sustainalytics);
    if (!isNaN(v)) {
      items.push(
        <Tooltip key="sa" title="Sustainalytics ESG Risk Rating (lower is better)" arrow>
          <span style={badgeStyle(saColor(v))}>{Math.round(v)}</span>
        </Tooltip>
      );
    }
  }

  if (msci && msci !== 'N/A') {
    items.push(
      <Tooltip key="msci" title="MSCI ESG Rating" arrow>
        <span style={badgeStyle(MSCI_COLORS[msci] || '#d9d9d9')}>{msci}</span>
      </Tooltip>
    );
  }

  if (refinitiv != null && refinitiv !== 'N/A') {
    const v = parseFloat(refinitiv);
    if (!isNaN(v)) {
      items.push(
        <Tooltip key="ref" title="Refinitiv ESG Score (0-100, higher is better)" arrow>
          <span style={badgeStyle(refColor(v))}>{Math.round(v)}</span>
        </Tooltip>
      );
    }
  }

  if (items.length === 0) return <Typography variant="caption" color="text.disabled">N/A</Typography>;
  return <Box display="flex" gap={0.5}>{items}</Box>;
};

const PAModal_ESG = ({ tickers, data, source = 'portfolio' }) => {
  const [esgData, setEsgData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('ticker');
  const [order, setOrder] = useState('asc');
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current || !tickers || tickers.length === 0) return;
    fetchedRef.current = true;

    setLoading(true);
    api.post('/pf_esg_view', { tickers })
      .then(res => setEsgData(res.data?.data || []))
      .catch(err => console.error('[PAModal_ESG] Failed to fetch ESG data:', err))
      .finally(() => setLoading(false));
  }, [tickers]);

  const rows = useMemo(() => {
    return esgData.map(d => {
      const position = data.find(p => (p.Ticker || '').toUpperCase() === (d.ticker || '').toUpperCase());
      const sa = d.SA_ESG_RR;
      const msci = d.MSCI_ESG_Rating;
      const ref = d.Refinitiv_ESG_score;
      const saNum = sa != null && sa !== 'N/A' ? parseFloat(sa) : null;
      const refNum = ref != null && ref !== 'N/A' ? parseFloat(ref) : null;
      const msciVal = msci && msci !== 'N/A' ? msci : null;

      return {
        ticker: d.ticker || '',
        company: d.company || position?.Company || '',
        sustainalytics: !isNaN(saNum) ? saNum : null,
        msci: msciVal,
        refinitiv: !isNaN(refNum) ? refNum : null,
        hasAny: (!isNaN(saNum) && saNum !== null) || msciVal || (!isNaN(refNum) && refNum !== null),
      };
    });
  }, [esgData, data]);

  const withCoverage = rows.filter(r => r.hasAny).length;

  const portfolioSummary = useMemo(
    () => computeESGSummary(rows, data),
    [rows, data]
  );

  const filtered = useMemo(() => {
    let list = rows;
    if (search) {
      const lower = search.toLowerCase();
      list = list.filter(r =>
        r.ticker.toLowerCase().includes(lower) ||
        r.company.toLowerCase().includes(lower)
      );
    }
    return [...list].sort((a, b) => {
      let av, bv;
      switch (orderBy) {
        case 'ticker': av = a.ticker; bv = b.ticker; break;
        case 'sustainalytics': av = a.sustainalytics ?? Infinity; bv = b.sustainalytics ?? Infinity; break;
        case 'msci': av = a.msci || 'ZZZ'; bv = b.msci || 'ZZZ'; break;
        case 'refinitiv': av = a.refinitiv ?? -Infinity; bv = b.refinitiv ?? -Infinity; break;
        default: av = a.ticker; bv = b.ticker;
      }
      if (av < bv) return order === 'asc' ? -1 : 1;
      if (av > bv) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, search, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder(field === 'sustainalytics' ? 'asc' : 'desc');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
        <CircularProgress />
        <Typography color="text.secondary" sx={{ ml: 2 }}>Loading ESG data…</Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No ESG data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ESGSummaryPanel summary={portfolioSummary} />

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          placeholder="Search ticker or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {withCoverage} of {rows.length} tickers with ESG data
        </Typography>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={3} mb={2} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={0.5}>
          <span style={badgeStyle('#52c41a')}>17</span>
          <Typography variant="caption" color="text.secondary">Sustainalytics (lower = better)</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <span style={badgeStyle('#52c41a')}>AA</span>
          <Typography variant="caption" color="text.secondary">MSCI Rating</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <span style={badgeStyle('#52c41a')}>93</span>
          <Typography variant="caption" color="text.secondary">Refinitiv (0-100, higher = better)</Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '55vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={orderBy === 'ticker'} direction={orderBy === 'ticker' ? order : 'asc'} onClick={() => handleSort('ticker')}>
                  Ticker
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                <Tooltip title="Sustainalytics ESG Risk Rating (lower is better)" arrow>
                  <TableSortLabel active={orderBy === 'sustainalytics'} direction={orderBy === 'sustainalytics' ? order : 'asc'} onClick={() => handleSort('sustainalytics')}>
                    Sustainalytics
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                <Tooltip title="MSCI ESG Rating" arrow>
                  <TableSortLabel active={orderBy === 'msci'} direction={orderBy === 'msci' ? order : 'asc'} onClick={() => handleSort('msci')}>
                    MSCI
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                <Tooltip title="Refinitiv ESG Score (0-100, higher is better)" arrow>
                  <TableSortLabel active={orderBy === 'refinitiv'} direction={orderBy === 'refinitiv' ? order : 'desc'} onClick={() => handleSort('refinitiv')}>
                    Refinitiv
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Combined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, idx) => (
              <TableRow key={row.ticker || idx} hover>
                <TableCell>
                  <ClickableTickerChip ticker={row.ticker} source={source} />
                  {row.company && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                      {row.company}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {row.sustainalytics != null ? (
                    <Tooltip title="Sustainalytics ESG Risk Rating (lower is better)" arrow>
                      <span style={badgeStyle(saColor(row.sustainalytics))}>
                        {Math.round(row.sustainalytics)}
                      </span>
                    </Tooltip>
                  ) : <Typography variant="caption" color="text.disabled">N/A</Typography>}
                </TableCell>
                <TableCell align="center">
                  {row.msci ? (
                    <Tooltip title="MSCI ESG Rating" arrow>
                      <span style={badgeStyle(MSCI_COLORS[row.msci] || '#d9d9d9')}>
                        {row.msci}
                      </span>
                    </Tooltip>
                  ) : <Typography variant="caption" color="text.disabled">N/A</Typography>}
                </TableCell>
                <TableCell align="center">
                  {row.refinitiv != null ? (
                    <Tooltip title="Refinitiv ESG Score (0-100, higher is better)" arrow>
                      <span style={badgeStyle(refColor(row.refinitiv))}>
                        {Math.round(row.refinitiv)}
                      </span>
                    </Tooltip>
                  ) : <Typography variant="caption" color="text.disabled">N/A</Typography>}
                </TableCell>
                <TableCell align="center">
                  <ESGBadges sustainalytics={row.sustainalytics} msci={row.msci} refinitiv={row.refinitiv} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

export default PAModal_ESG;
